import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../api/apiClient'; // Import API client
import Table from '../common/Table'; // Import Table component
import RTButton from '../common/RTButton'; // Import RTButton component
import RTSpinner from '../common/RTSpinner'; // Import RTSpinner component
import Menu from '../menu/Menu'; // Import Menu component
import '../common/rtCommon.css'; // Common styles
import './SelectBed.css'; // SelectBed specific styles

const SelectBed = ({ onSelect }) => {
  const [buildingList, setBuildingList] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [vacantBeds, setVacantBeds] = useState([]);
  const [selectedBed, setSelectedBed] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch building names
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        setVacantBeds([]);
        setLoadingBuildings(true);
        const response = await apiGet('/building/names');
        setBuildingList(response.data);
      } catch (error) {
        console.error('Failed to fetch buildings:', error);
        setError('Failed to load buildings');
      } finally {
        setLoadingBuildings(false);
      }
    };
    fetchBuildings();
  }, []);

  // Fetch vacant beds when a building is selected
  const fetchVacantBeds = async (buildingId) => {
    try {
      setLoadingBeds(true);
      setError(null);
      setSelectedBed(null); // Clear selection when changing building
      setSelectedRoomId(null);
      const response = await apiGet(`/building/vacant_beds/${buildingId}`);
      // Handle new response structure with vacant_beds array
      const bedsData = response.data.vacant_beds || response.data;
      setVacantBeds(bedsData);
    } catch (error) {
      console.error('Failed to fetch vacant beds:', error);
      setError('Failed to load vacant beds');
      setVacantBeds([]);
    } finally {
      setLoadingBeds(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  const handleSelectBed = () => {
    if (selectedBed && onSelect) {
      onSelect(selectedBed);
    }
  };

  const handleSelectedBed = (row) => {
    setSelectedRoomId(row.room_id); // Update the selected room ID
    setSelectedBed(row); // Update the selected bed state
  }

  const columns = [
    {
      Header: 'Room Name',
      accessor: 'room_name',
    },
    {
      Header: 'Available Beds',
      accessor: 'vacant_beds',
      Cell: ({ row }) => `${row.vacant_beds} of ${row.number_of_beds}`,
    },
    {
      Header: 'Room Mates',
      accessor: 'room_mates',
      Cell: ({ value }) => value && value.length > 0 ? value.join(', ') : 'None',
    },
    {
      Header: 'Bed Rent',
      accessor: 'bed_rent',
      Cell: ({ value }) => `₹ ${value}`,
    },
    {
      Header: 'Select',
      accessor: 'select',
      Cell: ({ row }) => (
        <input
          type="radio"
          name="bed-selection"
          checked={selectedRoomId === row.room_id}
          onChange={() => handleSelectedBed(row)}
          className="bed-radio"
        />
      ),
    },
  ];

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Select Bed</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={handleCancel}>
              &#8592; Back
            </RTButton>
          </div>
        </div>

        <div className="select-bed-content">
          <div className="rt-form-section">
            <div className="rt-form-group">
              <label htmlFor="building-select">
                Select Building <span style={{ color: 'red' }}>*</span>
              </label>
              {loadingBuildings ? (
                <div className="loading-container">
                  <RTSpinner fullScreen={true} />
                </div>
              ) : (
                <select
                  id="building-select"
                  className="rt-input"
                  value={selectedBuildingId || ''}
                  onChange={(e) => {
                    const buildingId = e.target.value;
                    setSelectedBuildingId(buildingId);
                    if (buildingId) {
                      fetchVacantBeds(buildingId);
                    } else {
                      setVacantBeds([]);
                    }
                  }}
                >
                  <option value=""> --- Select a Building --- </option>
                  {buildingList.map((building) => (
                    <option key={building.building_id} value={building.building_id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {error && (
            <div className="rt-error-message">
              {error}
            </div>
          )}

          {selectedBuildingId && (
            <div className="rt-table-section">
              {loadingBeds ? (
                <div className="loading-container" style={{ padding: '40px' }}>
                  <RTSpinner fullScreen={true} />
                  <p style={{ marginTop: '10px', color: '#666' }}>Loading available beds...</p>
                </div>
              ) : (
                <>
                  <Table
                    title="Available Beds"
                    columns={columns}
                    data={vacantBeds}
                    showSearch={true}
                    exportable={false}
                    showPagination={true}
                    noDataText="No vacant beds available in this building."
                  />
                  {vacantBeds.length > 0 && (
                    <div className="select-bed-actions">
                      <RTButton
                        variant="primary"
                        onClick={handleSelectBed}
                        disabled={!selectedBed}
                      >
                        Select Bed
                      </RTButton>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectBed;